import React, { FC } from 'react';
import {
  getReleasePeriod,
  HotelReleaseGroup,
  ReleaseGroupStage,
  ReleaseGroupStatus,
  ReleaseGroupType,
} from '../../../shared/hotel';
import { MaterialIcon } from '../../components';
import { TranslationKey, useTranslation } from '../../translations';
import { classNames } from '../../utils';
import { timeUntil } from './util';

import './WaveStatus.scss';

interface Props {
  readonly wave: HotelReleaseGroup;
}

export const WaveStatus: FC<Props> = ({ wave }) => {
  const stages: React.JSX.Element[] = [];
  const isFcfs = wave.type === ReleaseGroupType.FirstComeFirstServe;
  const wavePeriod = getReleasePeriod(wave, new Date());

  if (wave.entryStartAt) {
    stages.push(
      <WaveStatusItem
        currentStage={wavePeriod === ReleaseGroupStage.EntryPeriod}
        date={wave.entryStartAt}
        icon="assignment"
        key="entry-start"
        name={isFcfs ? 'early_entries_open' : 'entries_open'}
      />,
    );
  }

  if (wave.entryEndAt) {
    stages.push(
      <WaveStatusItem
        currentStage={wavePeriod === ReleaseGroupStage.PreReleaseReady}
        date={wave.entryEndAt}
        icon="assignment_late"
        key="entry-end"
        name={isFcfs ? 'early_entries_close' : 'entries_close'}
        nextStage={wavePeriod === ReleaseGroupStage.EntryPeriod}
      />,
    );
  }

  stages.push(
    <WaveStatusItem
      currentStage={wavePeriod === ReleaseGroupStage.ReleasePeriod}
      date={wave.releaseStartAt}
      icon="event_note"
      key="release-start"
      name="booking_period_starts"
      nextStage={wavePeriod === ReleaseGroupStage.PreReleaseReady}
    />,
    <WaveStatusItem
      currentStage={wave.status === ReleaseGroupStatus.Finished}
      date={wave.releaseEndAt}
      icon="event_busy"
      key="release-end"
      name="booking_period_ends"
      nextStage={wavePeriod === ReleaseGroupStage.ReleasePeriod}
    />,
  );

  return (
    <div className="wave-status">
      {stages.map((stage, idx) => (
        <React.Fragment key={stage.key}>
          {stage}
          {idx + 1 < stages.length && (
            <div className="wave-status-arrow">
              <MaterialIcon name="arrow_forward" />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

interface ItemProps {
  readonly name: TranslationKey;
  readonly icon: string;
  readonly date: Date;
  readonly currentStage?: boolean;
  readonly nextStage?: boolean;
}

const WaveStatusItem: FC<ItemProps> = ({ name, icon, date, currentStage, nextStage }) => {
  const { ts } = useTranslation();

  return (
    <div
      className={classNames(
        {
          'wave-status-item-current': currentStage,
          'wave-status-item-next': nextStage,
        },
        'wave-status-item',
      )}
    >
      <div className="wave-status-main">
        <div className="wave-status-icon">
          <MaterialIcon name={icon} />
        </div>
        <div className="wave-status-info">
          <span className="wave-status-name">{ts(name)}</span>
          <span className="wave-status-date">
            {date.toLocaleString(undefined, {
              timeZoneName: 'short',
              day: 'numeric',
              month: 'short',
              year: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
            })}
          </span>
        </div>
      </div>
      {currentStage && (
        <div className="wave-status-stage wave-stage-current">{ts('current_stage')}</div>
      )}
      {nextStage && (
        <div className="wave-status-stage wave-stage-next">
          {ts('common.time.in')} {timeUntil(date)}
        </div>
      )}
    </div>
  );
};
